import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import { ApplicationState } from '../rootReducer';
import { BlazemeterUserConfig } from '../../types/rest/blazeMeterApiResponse';
import { getBlazemeterApiUrl } from '../../utils/bzmUserConfigUtil';
import axios from '../../axios/axios-base';
import _ from 'lodash';
import { showToastNotification } from '../ui/toasts/toastsActions';
import { ToastType } from '../ui/toasts/toastsTypes';
import { setFiles, setFolders, setLoadingFiles } from './sharedFoldersReducer';
import { SharedFoldersFilePayload, SharedFoldersState } from './sharedFoldersTypes';
import { CreateFolderPayload, DEFAULT_LIMIT } from '@blazemeter/blaze-data-ui';
import { getAuth } from '../../axios/dev-auth';

const doBzmRequest = async (
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  path: string,
  getState: any,
  params?: any,
  data?: any,
) => {
  const bzmConfig = getState().blazeUserConfig.blazeUserConfig as BlazemeterUserConfig;
  const bzmApiUrl = getBlazemeterApiUrl(bzmConfig);
  return axios({
    method,
    baseURL: bzmApiUrl,
    url: path,
    params,
    withCredentials: true,
    auth: getAuth(),
    data,
  });
};

export const requestLoadFolders = async (limit: number, skip: number, workspaceId: number, getState: any) => {
  return doBzmRequest('GET', 'folders', getState, {
    limit,
    skip,
    workspaceId,
    hidden: true,
  });
};

export const loadFolders = (workspaceId: number, { skip, limit }: { skip: number; limit: number }) => {
  return async (dispatch: ThunkDispatch<ApplicationState, null, AnyAction>, getState: any): Promise<void> => {
    try {
      const response = await requestLoadFolders(limit, skip, workspaceId, getState);
      const sfData: SharedFoldersState = {
        skip: response.data.skip,
        totalSharedFolders: response.data.total,
        workspaceSharedFolders: response.data.result,
      };
      dispatch(setFolders(sfData));
    } catch (error) {
      const errorBody = _.get(error, 'response.data.error', error.toString());
      dispatch(showToastNotification(ToastType.Danger, 'Failed to load Shared Folders', errorBody));
    }
  };
};

export const createFolder = (workspaceId: number, sharedFolder: CreateFolderPayload) => {
  return async (dispatch: ThunkDispatch<ApplicationState, null, AnyAction>, getState: any): Promise<void> => {
    try {
      await doBzmRequest('POST', `folders`, getState, {}, sharedFolder);
      await dispatch(loadFolders(workspaceId, { skip: getState().sharedFolders.skip, limit: DEFAULT_LIMIT }));
    } catch (error) {
      const errorBody = _.get(error, 'response.data.error', error.toString());
      dispatch(showToastNotification(ToastType.Danger, 'Failed to delete Shared Folder File', errorBody));
    }
  };
};

export const fetchFolder = async (sharedFolderId: string, getState: any) => {
  const response = await doBzmRequest('GET', `folders/${sharedFolderId}/files`, getState);
  const sfData: SharedFoldersFilePayload = {
    folderId: sharedFolderId,
    files: response.data.result.files,
    folderName: response.data.result.name,
  };
  return sfData;
};

export const loadFolder = (sharedFolderId: string) => {
  return async (dispatch: ThunkDispatch<ApplicationState, null, AnyAction>, getState: any): Promise<void> => {
    try {
      dispatch(setLoadingFiles(true));
      const sfData = await fetchFolder(sharedFolderId, getState);
      dispatch(setFiles(sfData));
    } catch (error) {
      const errorBody = _.get(error, 'response.data.error', error.toString());
      dispatch(showToastNotification(ToastType.Danger, 'Failed to load Shared Folders', errorBody));
    } finally {
      dispatch(setLoadingFiles(false));
    }
  };
};

export const deleteWorkspaceSharedFolder = (workspaceId: number, folderId: string) => {
  return async (dispatch: ThunkDispatch<ApplicationState, null, AnyAction>, getState: any): Promise<void> => {
    try {
      await doBzmRequest('DELETE', `folders/${folderId}`, getState);
      await dispatch(loadFolders(workspaceId, { skip: getState().sharedFolders.skip, limit: DEFAULT_LIMIT }));
    } catch (error) {
      const errorBody = _.get(error, 'response.data.error', error.toString());
      dispatch(showToastNotification(ToastType.Danger, 'Failed to delete Shared Folder', errorBody));
    }
  };
};

export const deleteWorkspaceSharedFolderFile = (sharedFolderId: string, fileName: string) => {
  return async (dispatch: ThunkDispatch<ApplicationState, null, AnyAction>, getState: any): Promise<void> => {
    try {
      await doBzmRequest('POST', `folders/${sharedFolderId}/delete-file`, getState, {}, { fileName: fileName });
      await dispatch(loadFolder(sharedFolderId));
    } catch (error) {
      const errorBody = _.get(error, 'response.data.error', error.toString());
      dispatch(showToastNotification(ToastType.Danger, 'Failed to delete Shared Folder File', errorBody));
    }
  };
};

export const uploadFileToSharedFolder = ({ id, file }: { id: string; file: File }) => {
  return async (dispatch: ThunkDispatch<ApplicationState, null, AnyAction>, getState: any): Promise<void> => {
    try {
      const form = new FormData();
      form.append('files[]', file);
      await doBzmRequest('POST', `/folders/${id}/files`, getState, {}, form);
      await dispatch(loadFolder(id));
    } catch (error) {
      const errorBody = _.get(error, 'response.data.error', error.toString());
      dispatch(showToastNotification(ToastType.Danger, 'Failed to upload Shared Folder File', errorBody));
    }
  };
};
